import { ReactNode } from 'react';
import { SketchPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import { Check, Paintbrush, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';

// Shared chrome for the pattern forms — the same section title, slider row and
// grout-colour picker repeat in every pattern form; state stays in each form.

export const PatternSectionTitle = ({ children }: { children: ReactNode }) => (
    <h3 className="mb-3 text-sm font-medium text-foreground">{children}</h3>
);

export const PatternFieldLabel = ({ children }: { children: ReactNode }) => (
    <p className="mb-1.5 text-xs font-medium text-muted-foreground">{children}</p>
);

interface UnitInputProps extends React.ComponentProps<typeof Input> {
    unit?: string;
}

/** Number input with a unit suffix — the MUI endAdornment pattern. */
export const UnitInput = ({ unit, className, ...props }: UnitInputProps) => (
    <div className={`relative ${className ?? ''}`}>
        <Input {...props} className="pe-9" />
        {unit && (
            <span className="pointer-events-none absolute inset-y-0 end-2.5 flex items-center text-xs text-muted-foreground">
                {unit}
            </span>
        )}
    </div>
);

interface SliderWithInputProps {
    name: string;
    value: string;
    unit?: string;
    min: number;
    max: number;
    step: number;
    /** Evenly-spaced tick labels rendered under the track (e.g. 1m…4m). */
    markLabels?: string[];
    showInput?: boolean;
    onSliderChange: (name: string, value: number) => void;
    onInputChange?: React.ChangeEventHandler<HTMLInputElement>;
}

export const SliderWithInput = ({
    name,
    value,
    unit,
    min,
    max,
    step,
    markLabels,
    showInput = true,
    onSliderChange,
    onInputChange,
}: SliderWithInputProps) => (
    <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
            <Slider
                value={[Number(value)]}
                onValueChange={(v) => onSliderChange(name, v[0])}
                min={min}
                max={max}
                step={step}
            />
            {markLabels && (
                <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
                    {markLabels.map((label) => (
                        <span key={label}>{label}</span>
                    ))}
                </div>
            )}
        </div>
        {showInput && onInputChange && (
            <UnitInput
                value={value}
                name={name}
                onChange={onInputChange}
                unit={unit}
                className="w-28 shrink-0"
            />
        )}
    </div>
);

interface GroutColorFieldProps {
    color: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onColorChange: (color: any) => void;
}

export const GroutColorField = ({ color, open, onOpenChange, onColorChange }: GroutColorFieldProps) => {
    const { t } = useTranslation();
    return (
        <>
            <div className="flex items-center gap-4">
                <button
                    type="button"
                    aria-label={t('PATTERNS.PARTIALS.CHOOSE_COLOR')}
                    onClick={() => onOpenChange(true)}
                    className="relative size-10 shrink-0 cursor-pointer border-2 border-border transition-colors hover:border-foreground"
                    // The grout colour swatch shows the picked value — data, not chrome.
                    style={{ backgroundColor: color || '#CCCCCC' }}
                >
                    <span className="absolute -end-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border-2 border-background bg-foreground">
                        <Pencil className="size-2.5 text-background" />
                    </span>
                </button>
                <Button variant="outline" size="sm" onClick={() => onOpenChange(true)}>
                    <Paintbrush data-icon="inline-start" />
                    {t('PATTERNS.PARTIALS.CHOOSE_COLOR')}
                </Button>
            </div>

            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="w-auto">
                    <DialogHeader>
                        <DialogTitle>{t('PATTERNS.PARTIALS.CHOOSE_COLOR')}</DialogTitle>
                    </DialogHeader>
                    <SketchPicker
                        color={color || '#CCCCCC'}
                        onChange={onColorChange}
                        presetColors={['#FFFFFF', '#CCCCCC', '#999999', '#666666', '#333333', '#000000']}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            <Check data-icon="inline-start" />
                            {t('COMMON.CONFIRM')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
